import React, { ReactElement, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { CountryDropdown } from "react-country-region-selector";
import { Application } from "../Application";

import { object, string } from "yup";
import { InputErrorMessage } from "../../ui";
import { ApplicationService } from "../ApplicationService";

interface IProps {
	application: Application;
	jumpToStep?: any;
	nextStep: number;
}

export const StepTwo = (props: IProps): ReactElement => {
	const [errors, setErrors] = useState(null);
	const [changes, setChanges] = useState(0);

	const applicationService = new ApplicationService();

	const applyingAsOptions = ["An Individual", "A Company"];
	const preferredLocationOptions = ["UCC", "Your Training Location"];
	const campusesOptions = [
		"Kingston",
		"Mandeville",
		"May Pen",
		"Montego Bay",
		"Ocho Rios",
		"(Outside Jamaica)",
	];
	const studyPreferenceOptions = [
		"Online",
		"Evening",
		"Morning",
		"Afternoon",
	];

	const validateStep = () => {
		let schemaOptions: any;
		let schemaObject: any;
		if (props.application.applyingAs === applyingAsOptions[0]) {
			schemaOptions = {
				studyPreference: string().required(
					"Please select your preferred study time",
				),
				preferredUccCampus: string().required(
					"Please select your preferred UCC Campus",
				),
				applyingAs: string().required(
					"Please select if you are applying as an Individual or a Company",
				),
			};

			schemaObject = {
				studyPreference: props.application.studyPreference,
				preferredUccCampus: props.application.preferredUccCampus,
				applyingAs: props.application.applyingAs,
			};
		} else if (props.application.applyingAs === applyingAsOptions[1]) {
			schemaOptions = {
				companyName: string().required(
					"Please enter your company name",
				),
				companyPosition: string().required(
					"Please enter your position in the company",
				),
				sessionOption: string().required(
					"Please selection your session option",
				),
			};

			schemaObject = {
				companyName: props.application.companyName,
				companyPosition: props.application.companyPosition,
				sessionOption: props.application.sessionOption,
			};

			// Customised Training

			if (props.application.sessionOption === "Customised Training") {
				schemaOptions.preferredLocation = string().required(
					"Please select your preferred study location",
				);
				schemaObject.preferredLocation =
					props.application.preferredLocation;

				// Preferred Location == UCC
				if (
					props.application.preferredLocation ===
					preferredLocationOptions[0]
				) {
					schemaOptions.preferredUccCampus = string().required(
						"Please select your preferred UCC Campus",
					);
					schemaObject.preferredUccCampus =
						props.application.preferredUccCampus;
				}
				if (
					props.application.preferredLocation ===
					preferredLocationOptions[1]
				) {
					schemaOptions.trainingLocationAddress = string().required(
						"Please enter your training location address",
					);
					schemaOptions.trainingLocationCity = string().required(
						"Please enter your training location city",
					);
					schemaOptions.trainingLocationCountry = string().required(
						"Please enter your training location country",
					);
					schemaObject.trainingLocationAddress =
						props.application.trainingLocationAddress;
					schemaObject.trainingLocationCity =
						props.application.trainingLocationCity;
					schemaObject.trainingLocationCountry =
						props.application.trainingLocationCountry;
				}
			}
		} else {
			schemaOptions = {
				applyingAs: string().required(
					"Please select if you are applying as an Individual or a Company",
				),
			};
			schemaObject = { applyingAs: props.application.applyingAs };
		}

		const schema = object(schemaOptions);

		schema
			.validate(schemaObject, { abortEarly: false })
			.then(valid => {
				if (valid) {
					setErrors(null);

					if (props.application.currentStep < 2) {
						props.application.currentStep = 2;
					}
					applicationService.updateApplication(props.application);
					props.jumpToStep(props.nextStep);
				}
			})
			.catch(err => {
				setErrors(err);
			});
	};

	const handleChangeApplyingAs = (event: any) => {
		props.application.applyingAs = event.target.value;
		if (event.target.value === applyingAsOptions[1]) {
			props.application.courseFeePaymentOption = "Company Sponsorship";
			props.application.studyPreference = "";
			props.application.preferredUccCampus = "";

			props.application.companyName = "";
			props.application.companyPosition = "";
			props.application.trainingLocationAddress = "";
			props.application.trainingLocationCity = "";
			props.application.trainingLocationCountry = "";
		} else {
			props.application.courseFeePaymentOption = "";
		}

		setChanges(changes + 1);
	};

	const handleChangepreferredUccCampus = (event: any) => {
		props.application.preferredUccCampus = event.target.value;
		setChanges(changes + 1);
	};

	const handleChangeStudyPreference = (event: any) => {
		props.application.studyPreference = event.target.value;
		setChanges(changes + 1);
	};

	const handleChangeSessionOption = (sessOption: string) => {
		props.application.sessionOption = sessOption;

		if (sessOption === "Join Existing Class") {
			props.application.trainingLocationAddress = "";
			props.application.trainingLocationCity = "";
			props.application.trainingLocationCountry = "";
			props.application.preferredUccCampus = "";
			props.application.preferredLocation = "";
		}

		setChanges(changes + 1);
	};

	const handleChangePreferredLocation = (event: any) => {
		props.application.preferredLocation = event.target.value;
		if (event.target.value === preferredLocationOptions[0]) {
			props.application.trainingLocationAddress = "";
			props.application.trainingLocationCity = "";
			props.application.trainingLocationCountry = "";
		}

		if (event.target.value === preferredLocationOptions[1]) {
			props.application.preferredUccCampus = "";
		}

		setChanges(changes + 1);
	};

	return (
		<>
			<Form.Group controlId="applyingAs">
				<Form.Label>Applying as *</Form.Label>
				<Form.Control
					as="select"
					size="lg"
					defaultValue={props.application.applyingAs}
					onChange={handleChangeApplyingAs}
				>
					<option>-Select-</option>
					{applyingAsOptions.map(option => (
						<option value={option}>{option}</option>
					))}
				</Form.Control>
				<InputErrorMessage field="applyingAs" errors={errors} />
			</Form.Group>
			{props.application.applyingAs === applyingAsOptions[0] ? (
				<>
					<Form.Group controlId="preferredUccCampus">
						<Form.Label>Preferred UCC Campus *</Form.Label>
						<Form.Control
							size="lg"
							as="select"
							defaultValue={props.application.preferredUccCampus}
							onChange={handleChangepreferredUccCampus}
						>
							<option>-Select-</option>
							{campusesOptions.map(option => (
								<option value={option}>{option}</option>
							))}
						</Form.Control>
						<InputErrorMessage
							field="preferredUccCampus"
							errors={errors}
						/>
					</Form.Group>
					<Form.Group controlId="studyPreference">
						<Form.Label>Study Preference *</Form.Label>
						<Form.Control
							size="lg"
							as="select"
							defaultValue={props.application.studyPreference}
							onChange={handleChangeStudyPreference}
						>
							<option>-Select-</option>
							{studyPreferenceOptions.map(option => (
								<option value={option}>{option}</option>
							))}
						</Form.Control>
						<InputErrorMessage
							field="studyPreference"
							errors={errors}
						/>
					</Form.Group>{" "}
				</>
			) : null}

			{props.application.applyingAs === applyingAsOptions[1] ? (
				<>
					<h4 className="mt-4 mb-4">Company Information</h4>
					<Row>
						<Col sm={6} xs={12}>
							<Form.Group controlId="companyName">
								<Form.Label>Company Name *</Form.Label>
								<Form.Control
									size="lg"
									defaultValue={props.application.companyName}
									onChange={(event: any) => {
										props.application.companyName =
											event.target.value;
									}}
								/>
								<InputErrorMessage
									field="companyName"
									errors={errors}
								/>
							</Form.Group>
						</Col>
						<Col sm={6} xs={12}>
							<Form.Group controlId="companyPosition">
								<Form.Label>Your Position *</Form.Label>
								<Form.Control
									size="lg"
									defaultValue={
										props.application.companyPosition
									}
									onChange={(event: any) => {
										props.application.companyPosition =
											event.target.value;
									}}
								/>
								<InputErrorMessage
									field="companyPosition"
									errors={errors}
								/>
							</Form.Group>
						</Col>
						<Col sm={12} className="mt-4">
							<h5 className="mb-4">Session Option *</h5>
							{["Join Existing Class", "Customised Training"].map(
								(option, idx) => (
									<>
										<div
											key={`default-${idx}`}
											className="mb-3"
										>
											<Form.Check
												type="radio"
												id={`default-${idx}`}
												name="sessionOption"
												label={option}
												value={option}
												defaultChecked={
													props.application
														.sessionOption ===
													option
												}
												onChange={() =>
													handleChangeSessionOption(
														option,
													)
												}
											/>
										</div>
									</>
								),
							)}
							<InputErrorMessage
								field="sessionOption"
								errors={errors}
							/>
						</Col>
					</Row>

					{props.application.sessionOption ===
					"Customised Training" ? (
						<>
							<Row>
								<Col sm={12} className="mb-4 mt-4">
									<Form.Group controlId="preferredLocation">
										<Form.Label>
											Preferred Location *
										</Form.Label>
										<Form.Control
											size="lg"
											as="select"
											defaultValue={
												props.application
													.preferredLocation
											}
											onChange={
												handleChangePreferredLocation
											}
										>
											<option>-Select-</option>
											{preferredLocationOptions.map(
												option => (
													<option value={option}>
														{option}
													</option>
												),
											)}
										</Form.Control>
										<InputErrorMessage
											field="preferredLocation"
											errors={errors}
										/>
									</Form.Group>
								</Col>
								{props.application.preferredLocation ===
								preferredLocationOptions[0] ? (
									<Col sm={12} className="mb-3">
										<Form.Group controlId="preferredUccCampus">
											<Form.Label>
												Preferred UCC Campus *
											</Form.Label>
											<Form.Control
												size="lg"
												as="select"
												defaultValue={
													props.application
														.preferredUccCampus
												}
												onChange={
													handleChangepreferredUccCampus
												}
											>
												<option>-Select-</option>
												{campusesOptions.map(option => (
													<option value={option}>
														{option}
													</option>
												))}
											</Form.Control>
											<InputErrorMessage
												field="preferredUccCampus"
												errors={errors}
											/>
										</Form.Group>
									</Col>
								) : (
									<>
										{props.application.preferredLocation ===
										preferredLocationOptions[1] ? (
											<>
												<Col sm={12} className="mb-2">
													<h5 className="mb-4">
														Training Location
													</h5>
													<Form.Group controlId="trainingLocationAddress">
														<Form.Label>
															Address *
														</Form.Label>
														<Form.Control
															size="lg"
															defaultValue={
																props
																	.application
																	.trainingLocationAddress
															}
															onChange={(
																event: any,
															) =>
																(props.application.trainingLocationAddress =
																	event.target.value)
															}
														/>
														<InputErrorMessage
															field="trainingLocationAddress"
															errors={errors}
														/>
													</Form.Group>
												</Col>
												<Col sm={12} className="mb-2">
													<Form.Group controlId="trainingLocationCity">
														<Form.Label>
															City *
														</Form.Label>
														<Form.Control
															size="lg"
															defaultValue={
																props
																	.application
																	.trainingLocationCity
															}
															onChange={(
																event: any,
															) =>
																(props.application.trainingLocationCity =
																	event.target.value)
															}
														/>
														<InputErrorMessage
															field="trainingLocationCity"
															errors={errors}
														/>
													</Form.Group>
												</Col>
												<Col sm={12} className="mb-2">
													<Form.Group controlId="trainingLocationCountry">
														<Form.Label>
															Country *
														</Form.Label>
														<CountryDropdown
															classes="form-control form-control-lg"
															value={
																props
																	.application
																	.trainingLocationCountry as string
															}
															priorityOptions={[
																"JM",
															]}
															onChange={val => {
																props.application.trainingLocationCountry = val;
																setChanges(
																	changes + 1,
																);
															}}
														/>
														<InputErrorMessage
															field="trainingLocationCountry"
															errors={errors}
														/>
													</Form.Group>
												</Col>
											</>
										) : null}
									</>
								)}
							</Row>
						</>
					) : null}
				</>
			) : null}

			<Row>
				<Col>
					<Button
						variant="primary"
						size="lg"
						className="float-right btn-next"
						onClick={validateStep}
					>
						Next
					</Button>
				</Col>
			</Row>
		</>
	);
};
